import type { ReactNode } from 'react';
import { Form } from 'antd';
import type { CollectionOut } from '@/api/generated/schemas';
import { OrderedRelationRows } from './ordered_relation/OrderedRelationRows';
import { CollectionPicker } from './ordered_relation/CollectionPicker';
import { CollectionRowContent } from './collections/CollectionRowContent';
import { collectionIssues } from './collections/collectionFacts';
import { useLineupCollections } from './collections/useLineupCollections';
import { orderedRowIds, type OrderedRow } from './orderedRows';

/**
 * Consumer rule over the resolved collection: returns a tag when the entry is
 * unfit for that consumer's surface, `null` when it is fine. A firing
 * validator both shows its tag and marks the row as an error. `undefined`
 * means the collection is gone.
 */
export type CollectionValidator = (
  collection: CollectionOut | undefined
) => ReactNode | null;

interface OrderedCollectionCardsFieldProps {
  /** Form.List field name, e.g. `sections`. */
  name: string;
  /** Ids already on the record, so their option labels resolve. */
  existingCollectionIds?: number[];
  /** Surface-specific rules. Deleted and non-enabled entries are flagged
   * without them. */
  validators?: CollectionValidator[];
  /** One count query per row — off for consumers with long lists. */
  showCount?: boolean;
  /** Control docked beside a picked card, e.g. a section's display mode. The
   * resolved collection rides along so the control can warn without a second
   * lookup; it is undefined while the lookup is in flight or the collection
   * is gone. */
  renderRowExtra?: (row: {
    index: number;
    collectionId: number;
    collection: CollectionOut | undefined;
  }) => ReactNode;
  /** Seed values for a row created from the add button. */
  newRowDefaults?: Record<string, unknown>;
}

/**
 * Ordered collections as card rows: name, kind, publication count,
 * moderation tags, double-click to re-pick. Owns the lookups behind the live
 * rows. Publication sibling: `OrderedPublicationCardsField`.
 */
export const OrderedCollectionCardsField = ({
  name,
  existingCollectionIds,
  validators = [],
  showCount = true,
  renderRowExtra,
  newRowDefaults,
}: OrderedCollectionCardsFieldProps) => {
  const form = Form.useFormInstance();
  // Watched, not the record, so a just-picked collection renders before any
  // save.
  const rows = Form.useWatch<Array<OrderedRow<'collection_id'>> | undefined>(
    name,
    form
  );
  // Before the store initialises there is nothing to watch; the record's own
  // ids keep the first paint from rendering every row as unresolved.
  const lookups = useLineupCollections(
    rows ? orderedRowIds(rows, 'collection_id') : (existingCollectionIds ?? [])
  );

  // A row whose lookup has not landed yet says nothing — it is not "gone".
  const unresolved = (collectionId: number) =>
    !lookups.collectionById.has(collectionId) && lookups.isLoading;

  const verdicts = (collectionId: number) =>
    unresolved(collectionId)
      ? []
      : validators
          .map((validate) => validate(lookups.collectionById.get(collectionId)))
          .filter((verdict) => verdict != null);

  const hasIssue = (collectionId: number) =>
    !unresolved(collectionId) &&
    (collectionIssues(lookups.collectionById.get(collectionId)).length > 0 ||
      verdicts(collectionId).length > 0);

  return (
    <OrderedRelationRows
      name={name}
      valueField="collection_id"
      i18nPrefix="user_catalog.common.collections_editor"
      picker={<CollectionPicker preselectedIds={existingCollectionIds} />}
      renderRow={(collectionId) => {
        const fired = verdicts(collectionId);
        return (
          <CollectionRowContent
            collectionId={collectionId}
            lookups={lookups}
            showCount={showCount}
            // Empty would still be truthy, and the row would draw a divider
            // with nothing after it.
            extra={
              fired.length > 0
                ? fired.map((verdict, index) => (
                    <span key={index}>{verdict}</span>
                  ))
                : undefined
            }
          />
        );
      }}
      rowError={hasIssue}
      renderRowExtra={
        renderRowExtra &&
        (({ index, value }) =>
          renderRowExtra({
            index,
            collectionId: value,
            collection: lookups.collectionById.get(value),
          }))
      }
      newRowDefaults={newRowDefaults}
    />
  );
};
